import { differenceInCalendarDays, parseISO, subDays } from 'date-fns';
import { DateRange } from '../../model/type/date-range.type';
import { CustomChartRangeSelection } from '../../model/type/chart-range-selection.type';
import { HormoneValueType } from '../../model/type/hormone-values.type';
import LinecolorBreakpoint from '../../model/type/lince-color-breakpoint';
import { DailyData } from '../../model/dto/daily-data';
import { ScanChartData } from '../../model/type/scan-chart-data.type';
import MathUtils from './math-utils';
import { Scan } from '../../model/dto/scan';
import { UserCycle } from '../../model/dto/user-cycle/user-cycle';

/**
 * The bleeding a patient actually reported, as opposed to the computed Period
 * Phase Band. Exported so the Hormone Trends day-column model marks the same
 * days as the pink shading does.
 */
export const BLEEDING_VALUES = ['Light', 'Medium', 'Heavy'];

export default class ChartUtils {
  static getCustomDateRange(selection: CustomChartRangeSelection): DateRange {
    const now = new Date();

    if (selection.type === 'range') {
      return {
        startDate: selection.startDate,
        endDate: selection.endDate,
      };
    }

    return {
      startDate: subDays(now, selection.days - 1),
      endDate: now,
    };
  }

  static getCycleDateRange(cycle: UserCycle): DateRange {
    return {
      startDate: parseISO(cycle.start_day),
      endDate: parseISO(cycle.end_day),
    };
  }

  static isValuePresent(
    hormoneType: HormoneValueType,
    dailyData?: DailyData
  ): boolean {
    return (
      dailyData !== undefined &&
      dailyData[hormoneType] !== undefined &&
      dailyData[hormoneType] !== null
    );
  }

  static isScanValuePresent(scan: Scan, hormoneType: HormoneValueType) {
    return (
      scan !== undefined &&
      scan[hormoneType] !== undefined &&
      scan[hormoneType] !== null
    );
  }

  static getLinecolorBreakpoints(
    firstMeasureIndex: number,
    lastMeasureIndex: number,
    chartData: any[]
  ) {
    const lineColorBreakPoints: LinecolorBreakpoint[] = [
      {
        percentage: firstMeasureIndex / (chartData.length - 1),
        isMeasured: true,
      },
    ];

    for (let i = firstMeasureIndex + 1; i <= lastMeasureIndex; i++) {
      if (
        chartData[i].measured === false &&
        lineColorBreakPoints[lineColorBreakPoints.length - 1].isMeasured
      ) {
        lineColorBreakPoints.push({
          percentage: (i - 1) / (chartData.length - 1),
          isMeasured: false,
        });
      } else if (
        chartData[i].measured === true &&
        !lineColorBreakPoints[lineColorBreakPoints.length - 1].isMeasured
      ) {
        lineColorBreakPoints.push({
          percentage: i / (chartData.length - 1),
          isMeasured: true,
        });
      }
    }

    lineColorBreakPoints.push({
      percentage: lastMeasureIndex / (chartData.length - 1),
      isMeasured: false,
    });

    return lineColorBreakPoints;
  }

  static getPeriodDays(dailyData: (DailyData | undefined)[]) {
    const periodStartDayIndexes: number[] = [0];
    const periodEndingDayIndexes: number[] = [];

    let foundStartingDay = false;

    for (let i = 0; i < dailyData.length; i++) {
      if (BLEEDING_VALUES.includes(dailyData[i]?.bleeding ?? '')) {
        if (!foundStartingDay) {
          periodStartDayIndexes[0] = i;
        } else {
          periodStartDayIndexes.push(i);
        }

        foundStartingDay = true;

        while (i < dailyData.length) {
          if (!BLEEDING_VALUES.includes(dailyData[i]?.bleeding ?? '')) {
            periodEndingDayIndexes.push(i - 1);
            break;
          }
          i++;
        }

        if (
          periodStartDayIndexes[0] !== 0 &&
          periodEndingDayIndexes.length < periodStartDayIndexes.length
        ) {
          periodEndingDayIndexes.push(i - 1);
        }
      }
    }

    return { periodStartDayIndexes, periodEndingDayIndexes };
  }

  static getWindowLength(startDate: Date, endDate: Date) {
    return differenceInCalendarDays(endDate, startDate) + 1;
  }

  static mapScansToScanChartData(
    hormoneMinValue: number,
    hormoneMaxValue: number,
    index: number,
    hormoneType: HormoneValueType,
    dailyData?: DailyData
  ): ScanChartData[] {
    const dailyDataHormoneValue = dailyData ? dailyData[hormoneType] : null;

    return (
      dailyData?.scans?.reduce((res: ScanChartData[], scan) => {
        const scanValue = scan ? scan[hormoneType] : null;

        if (
          this.isScanValuePresent(scan, hormoneType) &&
          scanValue !== dailyDataHormoneValue
        ) {
          res.push({
            value: MathUtils.normalizeValue(
              scanValue ?? hormoneMinValue,
              hormoneMinValue,
              hormoneMaxValue
            ),
            index,
          });
        }

        return res;
      }, []) ?? []
    );
  }

  static getHormoneThreshold(max: number, percentage: number) {
    return max * percentage;
  }
}
